import { ReactElement, useState, useCallback, useEffect, useRef } from 'react';
import { VStack, HStack, Text, Box, Table as ChakraTable } from '@chakra-ui/react';
import { NovaIcon } from 'components/ui/novaIcon';
import { NovaIconButton } from 'components/ui/NovaIconButton';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import {
  useClassSearch,
  ClassData,
  ClassMember,
  useDeleteClass,
} from 'hooks/api/backend/managament/getClasses';
import { Table } from 'components/common/Table/Table';
import { Pagination } from 'components/common/Table/Pagination';
import { OrganizationManagementLayout } from 'components/OrganizationManagement/OrganizationManagementLayout';
import { createColumns } from 'utils/tableHelper';
import { AddIcon, DeleteIcon, SearchIcon } from 'assets/icons/v2';
import { Filters } from 'components/common/Table/Filters';
import { InputOption } from 'components/common/Table/FilterOptions/InputOption';
import { useNovaToast } from 'hooks/utils/useNovaToast';
import { toastContent } from 'config/toast';
import { ManagementStatusType } from 'types/StatusTypes';
import { ToastVariant } from 'types/Toast';
import { useModalService } from 'hooks/useModalService';
import { ModalVariant } from 'types/Modal';
import { ProtectedRoutes } from 'types/Routes';
import { NameList } from 'components/common/NameList/NameList';
import { DefaultCell } from 'components/common/Table/Cells/DefaultCell';
import { ActionButton } from 'components/atoms/managament/ActionButton';
import { NoSearchResult } from 'components/common/NoSearchResult';
import storage from 'utils/storage';
import { QueryErrorState } from 'components/common/QueryErrorState';

type ClassTableData = ClassData & {
  actionItem: ReactElement;
  studentsCount: string;
  teachersCount: string | ReactElement;
  guidesCount: string | ReactElement;
  expandedRow?: ReactElement;
};

const ExpandedClassRow = ({
  students,
  teachers,
  guides,
  searchQuery,
}: {
  students: ClassMember[];
  teachers: ClassMember[];
  guides: ClassMember[];
  searchQuery: string;
}): ReactElement => {
  const highlightMatch = (text: string): ReactElement => {
    if (!searchQuery) return <Text as="span">{text}</Text>;

    const lowerText = text.toLowerCase();
    const lowerQuery = searchQuery.toLowerCase();
    const index = lowerText.indexOf(lowerQuery);

    if (index === -1) return <Text as="span">{text}</Text>;

    return (
      <Text as="span">
        {text.substring(0, index)}
        <Box as="span" bg="#FCEACA" borderRadius={2} lineHeight="100%">
          {text.substring(index, index + searchQuery.length)}
        </Box>
        {text.substring(index + searchQuery.length)}
      </Text>
    );
  };

  return (
    <Box bg="#FAFAFA" py="l" px="15px">
      <VStack align="flex-start" gap="l">
        {students.length > 0 && (
          <HStack gap="10px">
            <Text textStyle="body.s.regular" color="text.primary">
              Lernende:
            </Text>
            <Text textStyle="body.s.medium" color="text.primary">
              {highlightMatch(`${students[0].firstName} ${students[0].lastName}`)}
            </Text>
          </HStack>
        )}
        {teachers.length > 0 && (
          <HStack gap="10px">
            <Text textStyle="body.s.regular" color="text.primary">
              Lehrende:
            </Text>
            <Text textStyle="body.s.medium" color="text.primary">
              {highlightMatch(`${teachers[0].firstName} ${teachers[0].lastName}`)}
            </Text>
          </HStack>
        )}
        {guides.length > 0 && (
          <HStack gap="10px">
            <Text textStyle="body.s.regular" color="text.primary">
              Praxisanleitende:
            </Text>
            <Text textStyle="body.s.medium" color="text.primary">
              {highlightMatch(`${guides[0].firstName} ${guides[0].lastName}`)}
            </Text>
          </HStack>
        )}
      </VStack>
    </Box>
  );
};

export const ClassDashboard = (): ReactElement => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const searchTimeoutRef = useRef<NodeJS.Timeout>();

  const toast = useNovaToast();
  const modalService = useModalService();
  const { mutateAsync: deleteClass } = useDeleteClass();

  const pageFromUrl = searchParams.get('p');
  const searchFromUrl = searchParams.get('s');

  const [searchQuery, setSearchQuery] = useState<string>(searchFromUrl ?? '');
  const selectedSchool = storage.getTenant();
  const [currentPage, setCurrentPage] = useState<number>(
    pageFromUrl ? parseInt(pageFromUrl, 10) : 1
  );

  const { data: classesData, isLoading, isError, error, refetch } = useClassSearch({
    schoolId: selectedSchool,
    page: currentPage,
    searchQuery: searchQuery.length >= 1 ? searchQuery : '',
  });

  const handleSearchChange = useCallback((e: string) => {
    setSearchQuery(e);
    setCurrentPage(1);
  }, []);

  const handleManageClass = (classId: string): void => {
    navigate(`${ProtectedRoutes.ORG_MANAGEMENT_CLASS_DETAIL}/${classId}`);
  };

  const handleDeleteClass = (classId: string, className: string): void => {
    modalService.show(ModalVariant.DELETE_CLASS, {
      className,
      onConfirm: async () => {
        try {
          await deleteClass({ classId });
          queryClient.invalidateQueries({ queryKey: ['classes'] });
          toast.show({
            description: 'Kurs erfolgreich gelöscht',
            variant: ToastVariant.SUCCESS,
          });
        } catch (error) {
          toast.show({
            description: 'Kurs konnte nicht gelöscht werden',
            variant: ToastVariant.ERROR,
          });
        }
      },
    });
  };

  const handleCreateClass = (): void => {
    if (!selectedSchool) {
      toast.show(toastContent.management[ManagementStatusType.SELECT_SCHOOL]);
      return;
    }

    modalService.show(ModalVariant.CLASS_CREATION);
  };

  const handlePageChange = (newPage: number): void => {
    setCurrentPage(newPage);
  };

  const classColumns = createColumns<ClassTableData>()({
    name: {
      header: 'Name',
      element: DefaultCell,
      getCellProps: () => ({
        textColor: 'text.primary',
        labelWeight: '500',
      }),
      sortDisabled: true,
    },
    studentsCount: {
      header: 'Lernende',
      sortDisabled: true,
    },
    teachersCount: {
      header: 'Lehrende',
      sortDisabled: true,
    },
    guidesCount: {
      header: 'Praxisanleitende',
      sortDisabled: true,
    },
    actionItem: {
      header: 'Aktion',
      sortDisabled: true,
    },
  });

  const clearInput = (): void => {
    setSearchQuery('');
  };

  const getTeachersDisplay = (classItem: ClassData): string | ReactElement => {
    const teacherCount = classItem.matchCount?.teachers ?? classItem.teachers.length;

    if (teacherCount === 0) {
      return '0';
    }

    if (searchQuery) {
      return teacherCount.toString();
    }

    const teacherNames = classItem.teachers.map(
      (teacher) => `${teacher.firstName} ${teacher.lastName}`
    );
    return <NameList names={teacherNames} maxVisible={1} />;
  };
  const getGuidesDisplay = (classItem: ClassData): string | ReactElement => {
    const guideCount = classItem.matchCount?.guides ?? classItem.guides.length;

    if (guideCount === 0) {
      return '0';
    }

    if (searchQuery) {
      return guideCount.toString();
    }

    const guideNames = classItem.guides.map((guide) => `${guide.firstName} ${guide.lastName}`);
    return <NameList names={guideNames} maxVisible={1} />;
  };

  const mappedClasses: ClassTableData[] = (classesData?.data || []).map((classItem: ClassData) => {
    const studentCount = classItem.matchCount?.students ?? classItem.students.length;
    return {
      ...classItem,
      studentsCount: studentCount === 0 ? '0' : studentCount.toString(),
      teachersCount: getTeachersDisplay(classItem),
      guidesCount: getGuidesDisplay(classItem),
      actionItem: (
        <NovaIconButton
          aria-label="Kurs löschen"
          variant="primary"
          size="s"
          onClick={(e) => {
            e.stopPropagation();
            handleDeleteClass(classItem.id, classItem.name);
          }}
          icon={DeleteIcon}
          iconProps={{ fill: 'none' }}
        />
      ),
      expandedRow: classItem.matchCount ? (
        <ChakraTable.Row key={`expanded-${classItem.id}`}>
          <ChakraTable.Cell colSpan={classColumns.length} padding={0}>
            <ExpandedClassRow
              students={classItem.students}
              teachers={classItem.teachers}
              guides={classItem.guides}
              searchQuery={searchQuery}
            />
          </ChakraTable.Cell>
        </ChakraTable.Row>
      ) : undefined,
    };
  });

  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    searchTimeoutRef.current = setTimeout(() => {
      const newParams = new URLSearchParams(searchParams);
      if (searchQuery) {
        newParams.set('s', searchQuery);
      } else {
        newParams.delete('s');
      }
      if (currentPage > 1) {
        newParams.set('p', currentPage.toString());
      } else {
        newParams.delete('p');
      }
      setSearchParams(newParams, { replace: true });
    }, 300);
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, currentPage]);

  const hasData = !!(classesData?.data && classesData.data.length > 0);
  const hasSearchQuery = !!searchQuery;
  const showNoClassesState = !isLoading && !hasData && !hasSearchQuery;
  const showNoResultsState = !isLoading && !hasData && hasSearchQuery;

  if (isError) {
    return <QueryErrorState error={error} onRetry={() => refetch()} />;
  }

  const getListContent = (): ReactElement => {
    if (showNoClassesState) {
      return (
        <VStack gap="m" align="center" my="50px">
          <Text textStyle="body.s.medium" color="text.quaternary">
            Noch keine Kurse vorhanden
          </Text>
          <Text textStyle="body.s.regular" color="text.quaternary">
            Erstelle jetzt deinen ersten Kurs, um Lernende zu verwalten und Inhalte zuzuweisen.
          </Text>
          <ActionButton title="Kurs erstellen" onClick={handleCreateClass} />
        </VStack>
      );
    }
    if (showNoResultsState) {
      return <NoSearchResult query={searchQuery} />;
    }
    return (
      <>
        <HStack gap="10px">
          <Text textStyle="body.s.regular" color="grey.50">
            {classesData?.data?.length || 0} Kurse
          </Text>
        </HStack>
        <Table<ClassTableData>
          columns={classColumns}
          data={mappedClasses}
          isDataLoading={isLoading}
          onRowClick={handleManageClass}
          tableRowProps={{
            _hover: {
              bg: 'background.canvas',
              cursor: 'pointer',
            },
          }}
        />
        {classesData?.totalPages && classesData.totalPages > 1 && (
          <Pagination
            totalPages={classesData.totalPages}
            currentPage={currentPage}
            onPageChange={handlePageChange}
          />
        )}
      </>
    );
  };

  return (
    <OrganizationManagementLayout
      pageTitle="Kurse"
      tabTitle="Kurse"
      handleInviteClick={handleCreateClass}
      inviteButtonText="Erstellen"
      inviteIcon={<NovaIcon icon={AddIcon} size="s" aria-hidden />}
    >
      <VStack align="left" bg="background.primary" w="full" gap="l" borderRadius={{ md: '2xl' }}>
        <Filters
          filters={[
            [
              {
                id: 'searchQuery',
                element: InputOption,
                placeholder: 'Kursname, Nachname, Vorname',
                value: searchQuery,
                options: [],
                disabled: !selectedSchool,
                onChange: handleSearchChange,
                icon: <NovaIcon icon={SearchIcon} size="m" aria-hidden />,
                onClear: clearInput,
              },
            ],
          ]}
        />

        {getListContent()}
      </VStack>
    </OrganizationManagementLayout>
  );
};
